// frontend/src/pages/Expenses.js
import React, { useState, useEffect } from 'react';
import {
  Box,
  Heading,
  Button,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  TableContainer,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  FormControl,
  FormLabel,
  Input,
  Select,
  Textarea,
  VStack,
  HStack,
  useDisclosure,
  useToast,
  Flex,
  Card,
  CardBody,
  CardHeader,
  Stat,
  StatLabel,
  StatNumber,
  StatHelpText,
  SimpleGrid,
  Badge,
  IconButton,
  Text,
  Spinner,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  InputGroup,
  InputLeftElement,
  Stack,
  Divider,
  useBreakpointValue,
} from '@chakra-ui/react';
import { 
  FiMoreVertical, 
  FiEdit2, 
  FiTrash2, 
  FiDownload,
  FiUpload,
  FiSearch,
  FiFilter,
} from 'react-icons/fi';
import { apiService } from '../services/apiService';
import { useAuth } from '../contexts/AuthContext';

export default function Expenses() {
  const [expenses, setExpenses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedExpense, setSelectedExpense] = useState(null);
  const [receiptFile, setReceiptFile] = useState(null);
  const { isOpen, onOpen, onClose } = useDisclosure();
  const toast = useToast();
  const { user } = useAuth();
  const lectureSeule = user?.role === 'commissaire' && !user?.is_admin;
  const isMobile = useBreakpointValue({ base: true, md: false });

  const [searchTerm, setSearchTerm] = useState('');
  const [filterCategory, setFilterCategory] = useState('all');
  const [filterMonth, setFilterMonth] = useState('all');
  const [filterYear, setFilterYear] = useState(new Date().getFullYear());

  const [formData, setFormData] = useState({
    date_depense: new Date().toISOString().split('T')[0],
    categorie: '',
    description: '',
    montant: '',
    fournisseur: '',
    mode_paiement: 'virement',
    reference_paiement: '',
    notes: '',
  });

  const categories = [
    { value: 'location', label: 'Location de l\'église', icon: '🏛️' },
    { value: 'chauffage', label: 'Chauffage / Électricité', icon: '🔥' },
    { value: 'sainte_cene', label: 'Sainte Cène', icon: '🍷' },
    { value: 'informatique', label: 'Matériel informatique', icon: '💻' },
    { value: 'fournitures', label: 'Fournitures de bureau', icon: '📎' },
    { value: 'entretien', label: 'Entretien / Nettoyage', icon: '🧹' },
    { value: 'communication', label: 'Communication', icon: '📢' },
    { value: 'transport', label: 'Transport', icon: '🚗' },
    { value: 'internet', label: 'Internet', icon: '🌐' },
    { value: 'seminaire', label: 'Séminaire', icon: '🎓' },
    { value: 'sonorisation', label: 'Sonorisation', icon: '🔊' },
    { value: 'autres', label: 'Autres dépenses', icon: '📦' },
  ];

  const paymentMethods = [
    { value: 'especes', label: 'Espèces' },
    { value: 'cheque', label: 'Chèque' },
    { value: 'virement', label: 'Virement bancaire' },
    { value: 'carte', label: 'Carte bancaire' },
    { value: 'prelevement', label: 'Prélèvement' },
  ];

  const years = Array.from({ length: 5 }, (_, i) => new Date().getFullYear() - i);
  const months = [
    'Janvier', 'Février', 'Mars', 'Avril', 'Mai', 'Juin',
    'Juillet', 'Août', 'Septembre', 'Octobre', 'Novembre', 'Décembre'
  ];

  useEffect(() => {
    fetchExpenses();
  }, [filterMonth, filterYear, filterCategory]);

  const fetchExpenses = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      if (filterMonth !== 'all') {
        params.append('month', filterMonth);
      }
      params.append('year', filterYear);
      if (filterCategory !== 'all') {
        params.append('categorie', filterCategory);
      }
      
      const data = await apiService.makeRequest(`/expenses?${params.toString()}`);
      setExpenses(data.expenses || data || []);
    } catch (err) {
      console.error('Error fetching expenses:', err);
      toast({
        title: 'Erreur',
        description: 'Erreur lors du chargement des dépenses',
        status: 'error',
        duration: 5000,
      });
    } finally {
      setLoading(false);
    }
  };

  const handleOpenModal = (expense = null) => {
    if (expense) {
      setSelectedExpense(expense);
      setFormData({
        date_depense: expense.date_depense.split('T')[0],
        categorie: expense.categorie,
        description: expense.description,
        montant: expense.montant,
        fournisseur: expense.fournisseur || '',
        mode_paiement: expense.mode_paiement,
        reference_paiement: expense.reference_paiement || '',
        notes: expense.notes || '',
      });
    } else {
      setSelectedExpense(null);
      setReceiptFile(null);
      setFormData({
        date_depense: new Date().toISOString().split('T')[0],
        categorie: '',
        description: '',
        montant: '',
        fournisseur: '',
        mode_paiement: 'virement',
        reference_paiement: '',
        notes: '',
      });
    }
    onOpen();
  };

  const handleReceiptChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) {
        toast({
          title: 'Erreur',
          description: 'Le fichier ne doit pas dépasser 5MB',
          status: 'error',
          duration: 3000,
        });
        return;
      }
      setReceiptFile(file);
    }
  };

  const handleSubmit = async () => {
    try {
      const expenseData = new FormData();
      
      Object.keys(formData).forEach(key => {
        if (formData[key] !== '' && formData[key] !== null) {
          expenseData.append(key, formData[key]);
        }
      });

      if (receiptFile) {
        expenseData.append('receipt', receiptFile);
      }

      if (selectedExpense) {
        await apiService.makeRequest(`/expenses/${selectedExpense.id}`, {
          method: 'PUT',
          body: expenseData,
        });
        toast({
          title: 'Dépense modifiée',
          description: 'La dépense a été mise à jour avec succès',
          status: 'success',
          duration: 3000,
        });
      } else {
        await apiService.makeRequest('/expenses', {
          method: 'POST',
          body: expenseData,
        });
        toast({
          title: 'Dépense ajoutée',
          description: 'La dépense a été enregistrée avec succès',
          status: 'success',
          duration: 3000,
        });
      }
      
      onClose();
      fetchExpenses();
      setReceiptFile(null);
    } catch (err) {
      toast({
        title: 'Erreur',
        description: err.message || 'Erreur lors de l\'enregistrement',
        status: 'error',
        duration: 5000,
      });
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer cette dépense ?')) {
      return;
    }

    try {
      await apiService.makeRequest(`/expenses/${id}`, {
        method: 'DELETE',
      });
      toast({
        title: 'Dépense supprimée',
        status: 'success',
        duration: 3000,
      });
      fetchExpenses();
    } catch (err) {
      toast({
        title: 'Erreur',
        description: err.message,
        status: 'error',
        duration: 5000,
      });
    }
  };

  const downloadReceipt = (expenseId) => {
    window.open(`${process.env.REACT_APP_API_URL}/expenses/${expenseId}/receipt`, '_blank');
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('fr-FR');
  };

  const formatAmount = (amount) => {
    return new Intl.NumberFormat('fr-FR', {
      style: 'currency',
      currency: 'EUR'
    }).format(amount || 0);
  };

  const getCategoryLabel = (category) => {
    const cat = categories.find(c => c.value === category);
    return cat ? `${cat.icon} ${cat.label}` : category;
  };

  const getPaymentMethodLabel = (method) => {
    const pm = paymentMethods.find(p => p.value === method);
    return pm ? pm.label : method;
  };

  const calculateStats = () => {
    if (expenses.length === 0) return null;

    const totalExpenses = expenses.reduce((sum, expense) => 
      sum + (parseFloat(expense.montant) || 0), 0
    );

    const expensesByCategory = expenses.reduce((acc, expense) => {
      const cat = expense.categorie;
      if (!acc[cat]) {
        acc[cat] = { count: 0, total: 0 };
      }
      acc[cat].count++;
      acc[cat].total += parseFloat(expense.montant) || 0;
      return acc;
    }, {});

    return {
      totalExpenses,
      count: expenses.length,
      byCategory: expensesByCategory,
    };
  };

  const stats = calculateStats();

  const filteredExpenses = expenses.filter(expense => {
    const matchesSearch = 
      expense.description.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (expense.fournisseur && expense.fournisseur.toLowerCase().includes(searchTerm.toLowerCase())) ||
      (expense.reference_paiement && expense.reference_paiement.toLowerCase().includes(searchTerm.toLowerCase()));
    return matchesSearch;
  });

  const renderRecu = (expense) => (
    expense.recu_path ? (
      <IconButton
        icon={<FiDownload />}
        size="sm"
        variant="ghost"
        colorScheme="blue"
        onClick={() => downloadReceipt(expense.id)}
        aria-label="Télécharger le reçu"
      />
    ) : (
      <Text fontSize="sm" color="gray.400">-</Text>
    )
  );

  const renderMenu = (expense) => lectureSeule ? null : (
    <Menu>
      <MenuButton
        as={IconButton}
        icon={<FiMoreVertical />}
        variant="ghost"
        size="sm"
        aria-label="Actions"
      />
      <MenuList>
        <MenuItem icon={<FiEdit2 />} onClick={() => handleOpenModal(expense)}>
          Modifier
        </MenuItem>
        <MenuItem icon={<FiTrash2 />} onClick={() => handleDelete(expense.id)} color="red.500">
          Supprimer
        </MenuItem>
      </MenuList>
    </Menu>
  );

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minH="400px">
        <Spinner size="xl" color="blue.500" />
      </Box>
    );
  }

  return (
    <Box>
      <Flex justify="space-between" align="center" mb={6}>
        <Heading size="lg">Gestion des Dépenses</Heading>
        {!lectureSeule && (
          <Button colorScheme="red" onClick={() => handleOpenModal()}>
            + Ajouter une dépense
          </Button>
        )}
      </Flex>

      {stats && (
        <SimpleGrid columns={{ base: 1, md: 2, lg: 4 }} spacing={4} mb={6}>
          <Card>
            <CardBody>
              <Stat>
                <StatLabel>Total dépenses</StatLabel>
                <StatNumber color="red.600">{formatAmount(stats.totalExpenses)}</StatNumber>
                <StatHelpText>{filterMonth === 'all' ? 'Année' : months[filterMonth - 1]} {filterYear}</StatHelpText>
              </Stat>
            </CardBody>
          </Card>
          <Card>
            <CardBody>
              <Stat>
                <StatLabel>Nombre de dépenses</StatLabel>
                <StatNumber>{stats.count}</StatNumber>
                <StatHelpText>Ce mois</StatHelpText>
              </Stat>
            </CardBody>
          </Card>
          <Card>
            <CardBody>
              <Stat>
                <StatLabel>Dépense moyenne</StatLabel>
                <StatNumber>{formatAmount(stats.totalExpenses / stats.count)}</StatNumber>
              </Stat>
            </CardBody>
          </Card>
          <Card>
            <CardBody>
              <Stat>
                <StatLabel>Catégorie principale</StatLabel>
                <StatNumber fontSize="md">
                  {Object.entries(stats.byCategory).length > 0 &&
                    getCategoryLabel(Object.entries(stats.byCategory)
                      .sort((a, b) => b[1].total - a[1].total)[0][0])}
                </StatNumber>
              </Stat>
            </CardBody>
          </Card>
        </SimpleGrid>
      )}

      {stats && stats.byCategory && Object.keys(stats.byCategory).length > 0 && (
        <Card mb={6}>
          <CardHeader>
            <Heading size="md">Répartition par catégorie</Heading>
          </CardHeader>
          <CardBody>
            <SimpleGrid columns={{ base: 1, md: 2, lg: 3 }} spacing={4}>
              {Object.entries(stats.byCategory)
                .sort((a, b) => b[1].total - a[1].total)
                .map(([category, data]) => (
                  <Box key={category} p={3} bg="gray.50" borderRadius="md">
                    <Text fontWeight="semibold">{getCategoryLabel(category)}</Text>
                    <Text fontSize="2xl" color="red.600" fontWeight="bold">
                      {formatAmount(data.total)}
                    </Text>
                    <Text fontSize="sm" color="gray.600">
                      {data.count} dépense(s)
                    </Text>
                  </Box>
                ))}
            </SimpleGrid>
          </CardBody>
        </Card>
      )}

      <Card mb={6}>
        <CardBody>
          <HStack spacing={4} wrap="wrap">
            <FormControl maxW="300px">
              <InputGroup>
                <InputLeftElement pointerEvents="none">
                  <FiSearch color="gray" />
                </InputLeftElement>
                <Input
                  placeholder="Rechercher..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </InputGroup>
            </FormControl>

            <FormControl maxW="200px">
              <Select
                value={filterCategory}
                onChange={(e) => setFilterCategory(e.target.value)}
              >
                <option value="all">Toutes les catégories</option>
                {categories.map(cat => (
                  <option key={cat.value} value={cat.value}>
                    {cat.label}
                  </option>
                ))}
              </Select>
            </FormControl>

            <FormControl maxW="150px">
              <Select
                value={filterMonth}
                onChange={(e) => setFilterMonth(e.target.value === 'all' ? 'all' : parseInt(e.target.value))}
              >
                <option value="all">Tous les mois</option>
                {months.map((month, idx) => (
                  <option key={idx} value={idx + 1}>{month}</option>
                ))}
              </Select>
            </FormControl>

            <FormControl maxW="120px">
              <Select
                value={filterYear}
                onChange={(e) => setFilterYear(parseInt(e.target.value))}
              >
                {years.map(year => (
                  <option key={year} value={year}>{year}</option>
                ))}
              </Select>
            </FormControl>
          </HStack>
        </CardBody>
      </Card>

      <Card>
        <CardBody p={0}>
          {isMobile ? (
            <Stack spacing={3} p={3} divider={<Divider />}>
              {filteredExpenses.length === 0 ? (
                <Text color="gray.500" textAlign="center" py={6}>Aucune dépense trouvée</Text>
              ) : (
                filteredExpenses.map((expense) => (
                  <Box key={expense.id}>
                    <HStack justify="space-between" align="flex-start" mb={1}>
                      <Badge colorScheme="purple" fontSize="sm" whiteSpace="normal">
                        {getCategoryLabel(expense.categorie)}
                      </Badge>
                      <Text fontWeight="bold" color="red.600">{formatAmount(expense.montant)}</Text>
                    </HStack>
                    <Text fontWeight="medium" mb={1}>{expense.description}</Text>
                    <Text fontSize="sm" color="gray.600">
                      {formatDate(expense.date_depense)} · {getPaymentMethodLabel(expense.mode_paiement)}
                      {expense.fournisseur ? ' · ' + expense.fournisseur : ''}
                    </Text>
                    <HStack justify="flex-end" spacing={1} mt={1}>
                      {renderRecu(expense)}
                      {renderMenu(expense)}
                    </HStack>
                  </Box>
                ))
              )}
            </Stack>
          ) : (
          <TableContainer>
            <Table variant="simple">
              <Thead bg="gray.50">
                <Tr>
                  <Th>Date</Th>
                  <Th>Catégorie</Th>
                  <Th>Description</Th>
                  <Th>Fournisseur</Th>
                  <Th isNumeric>Montant</Th>
                  <Th>Paiement</Th>
                  <Th>Reçu</Th>
                  <Th>Actions</Th>
                </Tr>
              </Thead>
              <Tbody>
                {filteredExpenses.length === 0 ? (
                  <Tr>
                    <Td colSpan={8} textAlign="center" py={10}>
                      <Text color="gray.500">Aucune dépense trouvée</Text>
                    </Td>
                  </Tr>
                ) : (
                  filteredExpenses.map((expense) => (
                    <Tr key={expense.id} _hover={{ bg: 'gray.50' }}>
                      <Td>{formatDate(expense.date_depense)}</Td>
                      <Td>
                        <Badge colorScheme="purple" fontSize="sm">
                          {getCategoryLabel(expense.categorie)}
                        </Badge>
                      </Td>
                      <Td fontWeight="medium">{expense.description}</Td>
                      <Td>{expense.fournisseur || '-'}</Td>
                      <Td isNumeric fontWeight="bold" color="red.600">
                        {formatAmount(expense.montant)}
                      </Td>
                      <Td fontSize="sm">
                        {getPaymentMethodLabel(expense.mode_paiement)}
                      </Td>
                      <Td>{renderRecu(expense)}</Td>
                      <Td>{renderMenu(expense)}</Td>
                    </Tr>
                  ))
                )}
              </Tbody>
            </Table>
          </TableContainer>
          )}
        </CardBody>
      </Card>

      <Modal isOpen={isOpen} onClose={onClose} size="xl" scrollBehavior="inside">
        <ModalOverlay />
        <ModalContent maxH="90vh">
          <ModalHeader>
            {selectedExpense ? 'Modifier la dépense' : 'Ajouter une dépense'}
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <VStack spacing={4}>
              <SimpleGrid columns={2} spacing={4} w="full">
                <FormControl isRequired>
                  <FormLabel>Date</FormLabel>
                  <Input
                    type="date"
                    value={formData.date_depense}
                    onChange={(e) => setFormData({ ...formData, date_depense: e.target.value })}
                  />
                </FormControl>

                <FormControl isRequired>
                  <FormLabel>Catégorie</FormLabel>
                  <Select
                    placeholder="Sélectionner une catégorie"
                    value={formData.categorie}
                    onChange={(e) => setFormData({ ...formData, categorie: e.target.value })}
                  >
                    {categories.map(cat => (
                      <option key={cat.value} value={cat.value}>
                        {cat.icon} {cat.label}
                      </option>
                    ))}
                  </Select>
                </FormControl>
              </SimpleGrid>

              <FormControl isRequired>
                <FormLabel>Description</FormLabel>
                <Input
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  placeholder="Ex: Location salle pour culte dominical"
                />
              </FormControl>

              <SimpleGrid columns={2} spacing={4} w="full">
                <FormControl isRequired>
                  <FormLabel>Montant (EUR)</FormLabel>
                  <Input
                    type="number"
                    step="0.01"
                    value={formData.montant}
                    onChange={(e) => setFormData({ ...formData, montant: e.target.value })}
                    placeholder="0.00"
                  />
                </FormControl>

                <FormControl>
                  <FormLabel>Fournisseur</FormLabel>
                  <Input
                    value={formData.fournisseur}
                    onChange={(e) => setFormData({ ...formData, fournisseur: e.target.value })}
                    placeholder="Nom du fournisseur"
                  />
                </FormControl>
              </SimpleGrid>

              <SimpleGrid columns={2} spacing={4} w="full">
                <FormControl isRequired>
                  <FormLabel>Mode de paiement</FormLabel>
                  <Select
                    value={formData.mode_paiement}
                    onChange={(e) => setFormData({ ...formData, mode_paiement: e.target.value })}
                  >
                    {paymentMethods.map(pm => (
                      <option key={pm.value} value={pm.value}>{pm.label}</option>
                    ))}
                  </Select>
                </FormControl>

                <FormControl>
                  <FormLabel>Référence paiement</FormLabel>
                  <Input
                    value={formData.reference_paiement}
                    onChange={(e) => setFormData({ ...formData, reference_paiement: e.target.value })}
                    placeholder="CHQ-123456 ou REF-789"
                  />
                </FormControl>
              </SimpleGrid>

              <FormControl>
                <FormLabel>Notes</FormLabel>
                <Textarea
                  value={formData.notes}
                  onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                  placeholder="Notes additionnelles..."
                  rows={3}
                />
              </FormControl>

              <FormControl>
                <FormLabel>
                  <HStack>
                    <FiUpload />
                    <Text>Joindre un reçu / facture (PDF, JPG, PNG - Max 5MB)</Text>
                  </HStack>
                </FormLabel>
                <Input
                  type="file"
                  accept=".pdf,.jpg,.jpeg,.png"
                  onChange={handleReceiptChange}
                  pt={1}
                />
                {receiptFile && (
                  <Text fontSize="sm" color="green.600" mt={2}>
                    ✓ Fichier sélectionné: {receiptFile.name}
                  </Text>
                )}
                {selectedExpense && selectedExpense.recu_path && !receiptFile && (
                  <Text fontSize="sm" color="blue.600" mt={2}>
                    Reçu existant disponible
                  </Text>
                )}
              </FormControl>
            </VStack>
          </ModalBody>

          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={onClose}>
              Annuler
            </Button>
            <Button 
              colorScheme="red" 
              onClick={handleSubmit}
              isDisabled={!formData.date_depense || !formData.categorie || !formData.description || !formData.montant}
            >
              Enregistrer
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Box>
  );
}